/**
 * User Balance Service - Checks user's OpenRouter balance for EUMPWU
 * 
 * EUMPWU = Each User Must Pay What They Use
 * Users must have sufficient balance in their personal OpenRouter key to send prompts.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

export interface UserBalanceResult {
  success: boolean;
  canSend: boolean;
  hasKey: boolean;
  balance: number;
  limit: number;
  usage: number;
  message?: string;
  /** Max AIs for anonymous try-before-pay users (null = no cap) */
  maxAis?: number | null;
  /** OpenRouter management key credits remaining (GET /credits), updated each global send */
  openRouterManagementRemaining?: number | null;
  /** Change vs previous send-triggered sample */
  openRouterManagementDelta?: number | null;
  openRouterManagementUpdatedAt?: string | null;
}

// Cache balance check results for 30 seconds to avoid excessive API calls
let cachedResult: UserBalanceResult | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL_MS = 30000; // 30 seconds

/**
 * Check if user has sufficient balance to send prompts
 * 
 * @returns UserBalanceResult with canSend flag and balance info
 */
export async function checkUserBalance(): Promise<UserBalanceResult> {
  // Return cached result if still valid
  const now = Date.now();
  if (cachedResult && (now - cacheTimestamp) < CACHE_TTL_MS) {
    console.log('📊 [UserBalance] Returning cached result');
    return cachedResult;
  }

  try {
    console.log('📊 [UserBalance] Checking user balance...');
    
    const functions = getFunctions();
    const checkBalance = httpsCallable(functions, 'checkUserBalanceEndpoint');
    
    const result = await checkBalance({});
    const data = result.data as UserBalanceResult;

    // Cache the result
    cachedResult = data;
    cacheTimestamp = now;

    console.log('📊 [UserBalance] Result:', {
      canSend: data.canSend,
      balance: data.balance,
      hasKey: data.hasKey,
    });

    return data;
  } catch (error: any) {
    console.error('📊 [UserBalance] Error checking balance:', error);
    
    // On error, return a safe default (can't send)
    return {
      success: false,
      canSend: false,
      hasKey: false,
      balance: 0,
      limit: 0,
      usage: 0,
      message: 'Unable to verify balance. Please try again.',
    };
  }
}

/**
 * Clear the cached balance result
 * Call this after user contributes funds
 */
export function invalidateBalanceCache(): void {
  cachedResult = null;
  cacheTimestamp = 0;
  console.log('📊 [UserBalance] Cache invalidated');
}

/**
 * Format balance for display
 */
export function formatBalance(balance: number): string {
  if (balance <= 0) return '$0.00';
  if (balance < 0.01) return `$${balance.toFixed(4)}`;
  return `$${balance.toFixed(2)}`;
}
